import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import splashIcon from "../assets/images/splash-icon.png";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { handleOAuthCallbackApi } from "../api/business.api";
import {
  discoverOnboardingLocationsApi,
  onboardingGoogleCallbackApi,
} from "../api/onboarding.api";
import { useAuth } from "../hooks/useAuth";
import useBusinessStore from "../store/businessStore";
import { showError, showSuccess } from "../utils/toast";
import type { ApiError } from "../types/api";
import Button from "../components/ui/Button";
import { getTheme } from "../config/theme";

const theme = getTheme("light").colors;

export default function OAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setActiveBusiness } = useBusinessStore();
  const { completeGoogleLogin, completeAppleLogin } = useAuth();

  const code = searchParams.get("code");
  const token = searchParams.get("token");
  const state = searchParams.get("state");
  const error = searchParams.get("error");

  const [status, setStatus] = useState("processing"); // processing, success, error
  const [message, setMessage] = useState("");
  const [flowType, setFlowType] = useState("business"); // business or login
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessed.current) return;

    // Determine flow type based on localStorage
    const isAppleLoginFlow = localStorage.getItem("apple_login_redirect") !== null;
    const isLoginFlow = localStorage.getItem("google_login_redirect") !== null || isAppleLoginFlow;
    const isOnboardingFlow = localStorage.getItem("onboarding_flow") === "true";
    setFlowType(isLoginFlow ? "login" : "business");

    if (error) {
      hasProcessed.current = true;
      // Abandoned/denied attempt - clear flags so they don't leak into a later, different flow
      localStorage.removeItem("google_login_redirect");
      localStorage.removeItem("apple_login_redirect");
      localStorage.removeItem("onboarding_flow");
      setStatus("error");
      setMessage(
        error === "access_denied"
          ? isLoginFlow
            ? "Access was denied. Please try signing in again."
            : "Access was denied. Please try connecting again."
          : `OAuth error: ${error}`,
      );
      return;
    }

    if (isAppleLoginFlow) {
      if (!token) {
        hasProcessed.current = true;
        setStatus("error");
        setMessage("No token received from Apple.");
        return;
      }
    } else if (!code) {
      hasProcessed.current = true;
      setStatus("error");
      setMessage("No authorization code received.");
      return;
    }

    hasProcessed.current = true;

    const handleCallback = async () => {
      try {
        if (isAppleLoginFlow && token) {
          // Apple's web callback already completed auth server-side and handed
          // back an access token directly (no code-exchange step needed here).
          const response = await completeAppleLogin(token);
          console.log("[Apple OAuth] completed login:", response);
          const redirectPath = localStorage.getItem("apple_login_redirect") || "/dashboard/overview";

          localStorage.removeItem("apple_login_redirect");

          setStatus("success");
          setMessage("Login successful!");
          showSuccess("Login successful!");

          setTimeout(() => {
            navigate(redirectPath, { replace: true });
          }, 1500);
        } else if (!code) {
          // Guarded against above for the apple flow, but re-checked here so
          // TypeScript narrows `code` to non-null for the branches below.
          throw new Error("No authorization code received.");
        } else if (isLoginFlow) {
          // Handle Google Login flow - use auth endpoint for auth token
          const response = await completeGoogleLogin({ code, state });
          console.log("[Google OAuth] completed login:", response);
          const redirectPath = localStorage.getItem("google_login_redirect") || "/dashboard/overview";

          // Clean up localStorage
          localStorage.removeItem("google_login_redirect");

          setStatus("success");
          setMessage(response?.raw?.message || "Login successful!");
          showSuccess(response?.raw?.message || "Login successful!");

          setTimeout(() => {
            navigate(redirectPath, { replace: true });
          }, 1500);
        } else if (isOnboardingFlow) {
          localStorage.removeItem("google_login_redirect");
          const callbackResponse = await onboardingGoogleCallbackApi({ code, state });
          const callbackPayload = callbackResponse?.data || callbackResponse || {};
          const accessToken =
            callbackPayload?.encryptedAccessToken ||
            callbackPayload?.accessToken ||
            callbackPayload?.googleAccessToken ||
            callbackPayload?.token;

          if (accessToken) {
            localStorage.setItem("onboarding_google_access_token", accessToken);
          }

          const discoveryResponse = await discoverOnboardingLocationsApi({ accessToken });
          const payload = discoveryResponse?.data || discoveryResponse || {};
          const discoveredLocations = payload?.locations || payload?.data || payload?.items || [];

          localStorage.setItem("onboarding_discovered_locations", JSON.stringify(discoveredLocations));

          const onboardingContextRaw = localStorage.getItem("onboarding_context");
          const onboardingContext = onboardingContextRaw ? JSON.parse(onboardingContextRaw) : null;

          setStatus("success");
          setMessage(discoveryResponse?.message || "Google connected successfully!");
          showSuccess(discoveryResponse?.message || "Google connected successfully!!");

          setTimeout(() => {
            navigate("/connect-google", {
              replace: true,
              state: {
                onboardingContext,
                discoveredLocations,
              },
            });
          }, 1200);
        } else {
          localStorage.removeItem("google_login_redirect");
          localStorage.removeItem("onboarding_flow");
          // Handle Business Connect flow - use business endpoint for connection
          const response = await handleOAuthCallbackApi({ code, state });

          // Retrieve stored business data from before OAuth redirect
          const storedBusiness = localStorage.getItem("pending_oauth_business");
          const business = storedBusiness ? JSON.parse(storedBusiness) : null;
          const businessId = response?.data?.businessId;

          // Update business with the returned businessId
          if (business && businessId) {
            business.id = businessId;
            setActiveBusiness(business);
          }

          // OAuth has now genuinely completed - mark the business as connected
          localStorage.setItem("google_connected", "true");
          localStorage.removeItem("google_connect_skipped");
          localStorage.removeItem("pending_oauth_business");

          const pendingMetaRaw = localStorage.getItem("pending_oauth_meta");
          const pendingMeta = pendingMetaRaw ? JSON.parse(pendingMetaRaw) : null;
          localStorage.removeItem("pending_oauth_meta");

          setStatus("success");
          setMessage(response?.message || "Business connected successfully!");
          showSuccess(response?.message || "Business connected successfully!");

          setTimeout(() => {
            navigate("/import-sync", {
              replace: true,
              state: {
                business,
                businessId,
                onboardingContext: pendingMeta?.onboardingContext,
                connectedCount: pendingMeta?.connectedCount || 1,
              },
            });
          }, 1500);
        }
      } catch (rawErr) {
        const err = rawErr as ApiError;
        setStatus("error");
        const errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          (isLoginFlow
            ? "Failed to complete sign in. Please try again."
            : "Failed to complete connection. Please try again.");
        setMessage(errorMessage);
        showError(errorMessage);
      }
    };

    handleCallback();
  }, [code, token, state, error, completeGoogleLogin, completeAppleLogin, navigate, setActiveBusiness]);

  const renderContent = () => {
    const isLogin = flowType === "login";

    switch (status) {
      case "processing":
        return (
          <>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full" style={{ background: theme.infoBg }}>
              <Loader2 className="h-6 w-6 animate-spin" style={{ color: theme.primary }} />
            </div>

            <h1 className="mt-6 text-2xl font-semibold tracking-tight sm:text-3xl" style={{ color: theme.text }}>
              {isLogin ? "Signing You In" : "Connecting Your Business"}
            </h1>

            <p className="mt-4 text-sm sm:text-base" style={{ color: theme.textMuted }}>
              {isLogin
                ? "Please wait while we complete the authentication..."
                : "Please wait while we complete the connection..."}
            </p>
          </>
        );

      case "success":
        return (
          <>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full" style={{ background: theme.successBg }}>
              <CheckCircle className="h-6 w-6" style={{ color: theme.success }} />
            </div>

            <h1 className="mt-6 text-2xl font-semibold tracking-tight sm:text-3xl" style={{ color: theme.text }}>
              {isLogin ? "Login Successful!" : "Connection Successful!"}
            </h1>

            <p className="mt-4 text-sm sm:text-base" style={{ color: theme.textMuted }}>{message}</p>

            <p className="mt-2 text-sm" style={{ color: theme.textLight }}>
              {isLogin ? "Redirecting to dashboard..." : "Redirecting..."}
            </p>
          </>
        );

      case "error":
      default:
        return (
          <>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full" style={{ background: theme.dangerBg }}>
              <XCircle className="h-6 w-6" style={{ color: theme.danger }} />
            </div>

            <h1 className="mt-6 text-2xl font-semibold tracking-tight sm:text-3xl" style={{ color: theme.text }}>
              {isLogin ? "Sign In Failed" : "Connection Failed"}
            </h1>

            <p className="mt-4 text-sm sm:text-base" style={{ color: theme.textMuted }}>{message}</p>

            <div className="mt-8 space-y-3">
              <Button variant="primary" theme={theme} onClick={() => navigate(isLogin ? "/login" : "/connect-google")} className="w-full py-3">
                Try Again
              </Button>

              <Button variant="secondary" theme={theme} onClick={() => navigate(isLogin ? "/register" : "/onboarding")} className="w-full py-3">
                {isLogin ? "Create Account" : "Restart Onboarding"}
              </Button>
            </div>
          </>
        );
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-8" style={{ background: theme.background }}>
      <div className="w-full max-w-md text-center">
        <img
          src={splashIcon}
          alt="ToronQ logo"
          className="mx-auto h-16 w-auto object-contain"
        />

        <div className="mt-8 rounded-2xl border p-8" style={{ borderColor: "#E7EAF0", background: theme.card }}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
